import Phaser from 'phaser';
import type {
  AtmospherePreset,
  AtmosphereBlendMode,
  RaysConfig,
} from '@/config/atmosphere.config';

/**
 * AtmosphereSystem — camera-fixed light overlay (winieta + god rays + tint).
 *
 * "Ożywia" planszę taniej niż particles: zamiast world-positioned cząstek (to
 * robi `AmbientFxSystem`) nakłada na CAŁY viewport kilka statycznych warstw
 * `scrollFactor=0`, których alpha/rotacja delikatnie oscylują. Efekt: kinowy
 * kadr, sączące się światło i "oddychający" nastrój biomu — bez nowych PNG
 * (tekstury `fx_vignette` / `fx_sunbeam` generowane proceduralnie w PreloadScene).
 *
 * **scrollFactor=0:** obiekty renderują się w SCREEN coords (0..cam.width,
 * 0..cam.height) niezależnie od scrolla świata — overlay przykleja się do
 * kamery, a nie do mapy. Pozycje/rozmiary przeliczane KAŻDĄ klatkę z
 * `cam.width/height`, więc resize i FIT scale-manager są obsłużone automatycznie
 * (analogicznie do mutacji spawn-zone w AmbientFxSystem).
 *
 * **Depth:** rysowane wysoko (nad światłem gry: rays 58 < tint 60 < vignette
 * 62), ale i tak SĄ POD React HUD — to osobna warstwa DOM (#ui z-10) nad
 * canvasem (#game z-0), więc żaden Phaser depth jej nie przykryje. Stąd overlay
 * barwi świat gry, ale nie zasłania przycisków/joysticka.
 *
 * **Pulsacja:** każda warstwa ma własną sin-oscylację alphy wokół peak/base.
 * `pulseDepth=0` → warstwa statyczna. God rays dodatkowo mają powolny sway
 * nachylenia (światło "kołysze się"), z fazą desync'owaną per-snop.
 *
 * **Tint vs tekstura:** tekstury bake'owane na biało (gradient w kanale alpha);
 * `color` z presetu aplikujemy `setTint` (white × tint = tint). Jedna tekstura
 * obsługuje wszystkie biomy.
 *
 * **Cleanup:** `destroy()` niszczy wszystkie GameObjecty. Wołane z
 * `WorldScene.cleanup()` (SHUTDOWN) + reset block na początku create()
 * (sesja 26d zombie-state pattern, identycznie jak ambientFx).
 *
 * **Edge cases:**
 *  - Brak tekstury `fx_vignette`/`fx_sunbeam` → warn + skip warstwy (gra działa).
 *  - Preset bez którejś warstwy (`undefined`) → po prostu jej nie tworzymy.
 *  - pixel_crt nie ma `rays` → brak snopów, czysty pixel-art.
 */
export class AtmosphereSystem {
  private readonly preset: AtmospherePreset;
  private readonly startTimeMs: number;

  private vignette: Phaser.GameObjects.Image | null = null;
  private tintRect: Phaser.GameObjects.Rectangle | null = null;

  /** Każdy snop: sprite + faza pulsu + faza sway + frakcja pozycji X na górnej krawędzi + znak nachylenia + bazowy tilt. */
  private readonly rayItems: Array<{
    img: Phaser.GameObjects.Image;
    pulsePhase: number;
    swayPhase: number;
    xFraction: number;
    baseTiltDeg: number;
  }> = [];

  private static readonly DEPTH_RAYS = 58;
  private static readonly DEPTH_TINT = 60;
  private static readonly DEPTH_VIGNETTE = 62;

  constructor(
    private readonly scene: Phaser.Scene,
    preset: AtmospherePreset,
  ) {
    this.preset = preset;
    this.startTimeMs = scene.time.now;
    this.build();
    // Pierwsze ułożenie pod aktualny viewport — żeby przed pierwszym update()
    // nic nie błysnęło w (0,0) lewym górnym rogu.
    this.layout(0);
  }

  private build(): void {
    const cam = this.scene.cameras.main;

    // ── Vignette ──────────────────────────────────────────────────────────────
    if (this.preset.vignette) {
      if (this.scene.textures.exists('fx_vignette')) {
        const v = this.preset.vignette;
        this.vignette = this.scene.add.image(cam.width / 2, cam.height / 2, 'fx_vignette')
          .setScrollFactor(0)
          .setDepth(AtmosphereSystem.DEPTH_VIGNETTE)
          .setTint(v.color)
          .setAlpha(v.alphaPeak);
      } else {
        console.warn('[Atmosphere] Pominięto vignette — brak tekstury fx_vignette');
      }
    }

    // ── Tint wash (pełnoekranowy prostokąt) ────────────────────────────────────
    if (this.preset.tint) {
      const t = this.preset.tint;
      this.tintRect = this.scene.add.rectangle(cam.width / 2, cam.height / 2, cam.width, cam.height, t.color)
        .setScrollFactor(0)
        .setDepth(AtmosphereSystem.DEPTH_TINT)
        .setBlendMode(toPhaserBlend(t.blendMode))
        .setAlpha(t.alphaPeak);
    }

    // ── God rays ───────────────────────────────────────────────────────────────
    if (this.preset.rays) {
      if (this.scene.textures.exists('fx_sunbeam')) {
        this.buildRays(this.preset.rays);
      } else {
        console.warn('[Atmosphere] Pominięto rays — brak tekstury fx_sunbeam');
      }
    }
  }

  private buildRays(r: RaysConfig): void {
    // Rozkład X na górnej krawędzi zależny od rogu: top-left grupuje snopy po
    // lewej (i przechyla w prawo), top-right po prawej, top centralnie.
    const span = this.originSpan(r.origin);
    const tiltSign = r.origin === 'top-right' ? -1 : 1;   // fan'ują się "w dół ekranu"

    for (let i = 0; i < r.count; i++) {
      // Równomierny rozkład w [span.lo, span.hi] z lekkim per-snop jitterem fazy.
      const f = r.count === 1 ? (span.lo + span.hi) / 2
        : span.lo + (span.hi - span.lo) * (i / (r.count - 1));
      // Bazowy tilt: snopy lekko się rozchylają (±) wokół r.tiltDeg.
      const spread = (i - (r.count - 1) / 2) * 6;          // 6° między sąsiednimi
      const baseTiltDeg = tiltSign * r.tiltDeg + spread;

      const img = this.scene.add.image(0, 0, 'fx_sunbeam')
        .setScrollFactor(0)
        .setDepth(AtmosphereSystem.DEPTH_RAYS)
        .setOrigin(0.5, 0)                                  // emanuje z górnej krawędzi w dół
        .setBlendMode(toPhaserBlend(r.blendMode))
        .setTint(r.color)
        .setAlpha(r.alphaPeak);

      this.rayItems.push({
        img,
        pulsePhase: Math.random() * Math.PI * 2,
        swayPhase: Math.random() * Math.PI * 2,
        xFraction: f,
        baseTiltDeg,
      });
    }
  }

  /** Zakres frakcji X (0..1) górnej krawędzi, w którym lądują kotwice snopów. */
  private originSpan(origin: RaysConfig['origin']): { lo: number; hi: number } {
    switch (origin) {
      case 'top-left':  return { lo: 0.05, hi: 0.45 };
      case 'top-right': return { lo: 0.55, hi: 0.95 };
      case 'top':
      default:          return { lo: 0.3, hi: 0.7 };
    }
  }

  /**
   * Per-frame tick — wołany z `WorldScene.update(time, dt)`.
   * Przelicza pozycje pod aktualny viewport (resize-safe) i aplikuje pulsy/sway.
   */
  update(time: number): void {
    this.layout((time - this.startTimeMs) / 1000);
  }

  /** Jedna metoda układa WSZYSTKO z aktualnego viewportu + elapsed (sek). */
  private layout(elapsedSec: number): void {
    const cam = this.scene.cameras.main;
    const w = cam.width;
    const h = cam.height;
    const TAU = Math.PI * 2;

    // ── Vignette: wycentruj + lekki nadmiar (1.06×) by uniknąć szpar na rogach.
    if (this.vignette && this.preset.vignette) {
      const v = this.preset.vignette;
      this.vignette.setPosition(w / 2, h / 2).setDisplaySize(w * 1.06, h * 1.06);
      this.vignette.setAlpha(pulse(v.alphaPeak, v.pulseDepth, v.pulseHz, elapsedSec, 0));
    }

    // ── Tint: pełen ekran.
    if (this.tintRect && this.preset.tint) {
      const t = this.preset.tint;
      this.tintRect.setPosition(w / 2, h / 2).setSize(w, h);
      this.tintRect.setAlpha(pulse(t.alphaPeak, t.pulseDepth, t.pulseHz, elapsedSec, 0));
    }

    // ── Rays: kotwica na górnej krawędzi, długość > przekątna (zawsze przecina ekran).
    if (this.preset.rays && this.rayItems.length > 0) {
      const r = this.preset.rays;
      const diag = Math.hypot(w, h);
      const beamLen = diag * 1.8;
      const beamWidth = Math.max(60, w * 0.16);
      const anchorY = -h * 0.1;                             // jasny koniec lekko nad ekranem

      for (const item of this.rayItems) {
        const tiltDeg = item.baseTiltDeg
          + r.swayDeg * Math.sin(elapsedSec * TAU * r.swayHz + item.swayPhase);
        item.img.setPosition(item.xFraction * w, anchorY);
        item.img.setDisplaySize(beamWidth, beamLen);
        item.img.setRotation(Phaser.Math.DegToRad(tiltDeg));
        item.img.setAlpha(
          pulse(r.alphaPeak, r.pulseDepth, r.pulseHz, elapsedSec, item.pulsePhase),
        );
      }
    }
  }

  /**
   * Niszczy wszystkie GameObjecty overlayu. Idempotent — wielokrotne wywołanie
   * jest no-opem (Phaser destroy() po pierwszym razie nic nie robi).
   */
  destroy(): void {
    this.vignette?.destroy();
    this.vignette = null;
    this.tintRect?.destroy();
    this.tintRect = null;
    for (const item of this.rayItems) {
      item.img.destroy();
    }
    this.rayItems.length = 0;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Oscylująca alpha wokół `base`. Zwraca wartość w [base*(1-depth), base],
 * tj. base to SZCZYT (peak), puls schodzi w dół. `phase` desync'uje instancje.
 */
function pulse(base: number, depth: number, hz: number, elapsedSec: number, phase: number): number {
  if (depth <= 0 || hz <= 0) return base;
  const wave = 0.5 + 0.5 * Math.sin(elapsedSec * Math.PI * 2 * hz + phase); // [0,1]
  return base * (1 - depth + depth * wave);
}

function toPhaserBlend(mode: AtmosphereBlendMode): Phaser.BlendModes {
  switch (mode) {
    case 'ADD':      return Phaser.BlendModes.ADD;
    case 'SCREEN':   return Phaser.BlendModes.SCREEN;
    case 'MULTIPLY': return Phaser.BlendModes.MULTIPLY;
    case 'NORMAL':
    default:         return Phaser.BlendModes.NORMAL;
  }
}
